import { CommonModule } from "@angular/common";
import { Component, ElementRef, EventEmitter, Input, Output, ViewChild } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { MatSelectModule } from "@angular/material/select";
import {
  AiImageAspectRatio,
  AiGeneratedImage,
  AiImageGenerationRequest,
  ImagesService,
} from "@company-box/services";
import { User } from "@company-box/models";

interface AiImageAspectRatioOption {
  label: string;
  value: AiImageAspectRatio;
}

@Component({
  selector: "app-ai-image-modal",
  standalone: true,
  templateUrl: "./ai-image-modal.component.html",
  styleUrls: ["./ai-image-modal.component.scss"],
  imports: [CommonModule, FormsModule, MatSelectModule],
})
export class AiImageModalComponent {
  @ViewChild("promptTextarea") private promptTextarea?: ElementRef<HTMLTextAreaElement>;

  @Input() containerId: string = "";
  @Input() user: User | undefined;
  @Output() hideModal: EventEmitter<void> = new EventEmitter();
  @Output() imageCreated: EventEmitter<AiGeneratedImage> = new EventEmitter();
  @Output() imageGenerated: EventEmitter<AiGeneratedImage> = new EventEmitter();

  prompt = "";
  lastPrompt = "";
  selectedAspectRatio: AiImageAspectRatio = "1:1";
  isGenerating = false;
  submitted = false;
  errorMessage = "";
  generatedImage: AiGeneratedImage | undefined;
  conversationId: string | undefined;

  aspectRatioOptions: AiImageAspectRatioOption[] = [
    { label: "Square — 1:1", value: "1:1" },
    { label: "Portrait — 3:4", value: "3:4" },
    { label: "Landscape — 4:3", value: "4:3" },
    { label: "Tall — 9:16", value: "9:16" },
    { label: "Wide — 16:9", value: "16:9" },
  ];

  constructor(private imagesService: ImagesService) {}

  async generateImage() {
    this.submitted = true;
    this.errorMessage = "";

    const prompt = this.prompt.trim() || (this.generatedImage ? this.lastPrompt : "");
    if (!prompt) {
      this.focusPrompt();
      return;
    }

    const request: AiImageGenerationRequest = {
      containerId: this.containerId,
      prompt,
      fileType: 'png',
      aspectRatio: this.selectedAspectRatio,
      ...(this.conversationId ? { conversationId: this.conversationId } : {}),
    };

    this.generatedImage = undefined;
    this.isGenerating = true;
    try {
      const response = await this.imagesService.generateAiImage(this.user?.id ?? 0, request);
      if (!response.image?.src) {
        this.errorMessage = "The generated image did not include a usable image URL.";
        return;
      }
      this.generatedImage = response.image;
      this.conversationId = response.conversationId ?? this.conversationId;
      this.lastPrompt = prompt;
      this.prompt = "";
      this.submitted = false;
      this.imageCreated.emit(response.image);
    } catch (error) {
      this.errorMessage = this.getErrorMessage(error);
    } finally {
      this.isGenerating = false;
    }
  }

  onPromptKeydown(event: KeyboardEvent) {
    if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) {
      event.preventDefault();
      if (!this.isGenerating) {
        this.generateImage();
      }
    }
  }

  startOver() {
    this.conversationId = undefined;
    this.generatedImage = undefined;
    this.prompt = "";
    this.lastPrompt = "";
    this.submitted = false;
    this.errorMessage = "";
    this.focusPrompt();
  }

  addPreviewToDesign() {
    if (!this.generatedImage?.src) {
      this.errorMessage = "Generate an image before adding it to your design.";
      return;
    }
    this.imageGenerated.emit(this.generatedImage);
  }

  close() {
    this.conversationId = undefined;
    this.hideModal.emit();
  }

  trackByAspectRatio(index: number, option: AiImageAspectRatioOption) {
    return option.value;
  }

  get aspectRatioLabel() {
    return "Aspect ratio";
  }

  get promptPlaceholder() {
    return "Example: soft watercolor citrus pattern with warm cream background";
  }

  get promptDescribedBy() {
    const ids = ["ai-image-prompt-help", "ai-image-prompt-warning"];
    if (this.showPromptError) {
      ids.push("ai-image-prompt-error");
    }
    return ids.join(" ");
  }

  get aspectRatioHelpText() {
    return "Choose the aspect ratio for generation.";
  }

  get showPromptError() {
    return this.submitted && !this.prompt.trim() && !this.generatedImage;
  }

  getShapePreviewClass(option: AiImageAspectRatioOption) {
    return `ratio-${option.value.replace(":", "-")}`;
  }

  private focusPrompt() {
    setTimeout(() => this.promptTextarea?.nativeElement.focus());
  }

  private getErrorMessage(error: unknown) {
    if (error instanceof Error) {
      return error.message;
    }
    return "We could not generate an image right now. Please try again.";
  }
}
